import {RunConfigService, moment} from '@nuskin/ns-util';
import {AdrService, AdrConstants, CartService} from '@nuskin/ns-shop';
import {getCachedConfigField} from '@nuskin/configuration-sdk';

/**
 *
 * AdrUtil
 *
 * @namespace AdrUtil
 */
const AdrUtil = function() {
    'use strict';

    // vars
    let $scope;
    // eslint-disable-next-line
    let $timeout;
    let months;
    let nsADRType;

    // ---------------------------------------------
    //
    //        Public Methods
    //
    // ---------------------------------------------
    const publicMethods = {

        /**
         * Initializes AdrUtil
         * @memberof AdrUtil
         * @example
         * AdrUtil.initAdrUtil(scope, nsAdrOptionsService, timeout, _nsADRType, nsAdrUtil, needToHandleExternalPayment)
         */
        // eslint-disable-next-line
        initAdrUtil: async function(scope, nsAdrOptionsService, timeout, _nsADRType, nsAdrUtil, needToHandleExternalPayment) {
            $scope = scope;
            $timeout = timeout;
            nsADRType = _nsADRType;
            $scope.adrType = nsADRType.getADRType();

            // no need to initialize adr stuff if no adr items.
            $scope.adr = AdrService.getAdrDraft();
            if (!$scope.adr) {
                return;
            }
            let lang = RunConfigService.getRunConfig().language;
            if (lang === 'zh') lang = 'zh-tw';
            months = moment().locale(lang + '_' + RunConfigService.getRunConfig().country).localeData()._monthsShort;
            if (months === undefined || months === null) {
                months = moment().locale(lang + '_' + RunConfigService.getRunConfig().country).localeData()._months;
            }
            $scope.adrFutureDate = null;
            $scope.adrTncCheckbox = false;
            $scope.firstShipDayText = '';
            $scope.monthlyShipDayText = '';
            $scope.tdcAdrTncText = $scope.tdc.termsncondition.adrTncText;
            $scope.tncCheckboxText = $scope.tdc.termsncondition.adrTncCheckboxText;
            $scope.tncADRURL = $scope.tdc.termsncondition.adrTncUrl;

            $scope.adrOptions = await nsAdrUtil.getAdrOptionsWithShipDays();
            // on  mix cart availableAdrDates is getting wiped out need to reset it.
            const adrOptions = $scope.adrOptions.getOptions();
            if (!$scope.availableAdrDates && adrOptions.availableShipDates) {
                setupRecurDate();
            }

            const contractLength = $scope.adrOptions.get(AdrConstants.CONTRACT_LENGTH);
            if (contractLength) {
                if (contractLength.value) {
                    $scope.adr.contractLength = contractLength.value;
                } else {
                    $scope.adr.contractLength = null;
                }
                if (contractLength.contractType) {
                    $scope.adr.contractType = contractLength.contractType;
                } else {
                    $scope.adr.contractType = null;
                }
            } else {
                $scope.adr.contractLength = null;
                $scope.adr.contractType = null;
            }

            CartService.setBusinessPortfolioAdr();

            $scope.hasOptionValue = hasOptionValue;
            $scope.changeAdrDate = changeAdrDate;
            $scope.changeContractLength = changeContractLength;
            $scope.showRecurDateSelect = showRecurDateSelect;
            if ($scope.adrOptions.get(AdrConstants.SHOW_CONTRACT_LENGTHS)) {
                $scope.adrContractLength = $scope.adrOptions.get(AdrConstants.CONTRACT_LENGTH);
                $scope.adrContractLengths = $scope.adrOptions.get(AdrConstants.CONTRACT_LENGTHS);
                initContractLength();
            }
        },

        /**
         * Initializes ADR Section
         * @memberof AdrUtil
         * @example
         * AdrUtil.initAdrSection()
         */
        initAdrSection: function() {
            setupRecurDate();
            if (nsADRType.equals($scope.adrType, AdrConstants.IMMEDIATE) || nsADRType.equals($scope.adrType, AdrConstants.DEFAULT)) {
                if ($scope.adrOptions.get(AdrConstants.USE_DFLT_FOR_IMMEDIATE_FIRST)) {
                    updateShipDateText($scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT), $scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT));
                } else if ($scope.adrOptions.get(AdrConstants.ALLOW_SI_RECURRING_DATE_CHANGE)) {
                    updateShipDateText($scope.adrOptions.get(AdrConstants.TODAY), $scope.adrFutureDate);
                } else {
                    updateShipDateText($scope.adrOptions.get(AdrConstants.TODAY), $scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT));
                }
            } else if (nsADRType.equals($scope.adrType, AdrConstants.FUTURE)) {
                updateShipDateText($scope.adrFutureDate, $scope.adrFutureDate);
            } else if (nsADRType.equals($scope.adrType, AdrConstants.ALWAYS)) {
                updateShipDateText($scope.adrOptions.get(AdrConstants.TODAY), $scope.adrFutureDate);
            }
        }
    };

    // ---------------------------------------------
    //
    //        Private Methods
    //
    // ---------------------------------------------

    /** *****************************************************************************************************************
     * This method sets up the ADR next recurrent date. For ADR ship Immediate, this will be the next date after the
     * immediate order is shipped. For ADR future, this is the next date available for shipping plus 2 days.
     ******************************************************************************************************************/
    function setupRecurDate() {
        $scope.adrFutureDate = $scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT);
        $scope.availableAdrDates = $scope.adrOptions.get(AdrConstants.AVAILABLE_SHIP_DATES);
        if ($scope.availableAdrDates && $scope.availableAdrDates !== undefined) {
            const isADRImmediate = $scope.adrType && $scope.adrType.isImmediate === AdrConstants.IMMEDIATE.isImmediate;

            // We only get the next available date for ADR future orders. ADR immediate we just use the date that was chosen.
            if (getCachedConfigField('defaultNextAvailableFuture') && !isADRImmediate) {
                $scope.adrFutureDate = getNextAvailableDate($scope.availableAdrDates, $scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT));
            } else {
                $scope.adrFutureDate = getDateChosen($scope.availableAdrDates, $scope.adrOptions.get(AdrConstants.DFLT_RECURRING_DT));
            }
        }
        $scope.adr.deliveryDate = $scope.adrFutureDate.formatted;
    }

    function getNextAvailableDate(availableDates, desiredDate) {
        availableDates = availableDates.sort(function(a, b) {
            return a.dayOfMonth - b.dayOfMonth;
        });
        const date = new Date();
        const dayOfMonth = date.getDate();
        const month = date.getMonth() + 1;
        if (availableDates) {
            for (let i = 0; i < availableDates.length; i++) {
                if (availableDates[i].dayOfMonth > dayOfMonth && availableDates[i].month === month) {
                    return availableDates[i];
                }
            }
        }

        // If no dates available for current month then find default ship date the old way.
        return getDateChosen(availableDates, desiredDate);
    }

    function hasOptionValue(option, value) {
        return $scope.adrOptions && $scope.adrOptions.get ? $scope.adrOptions.get(option) === value : false;
    }

    function showRecurDateSelect() {
        return nsADRType.equals($scope.adrType, AdrConstants.FUTURE) ||
            nsADRType.equals($scope.adrType, AdrConstants.ALWAYS) ||
               hasOptionValue('allowShipImmediateRecurringDateChange', true);
    }

    function changeAdrDate() {
        const shipDate = nsADRType.equals($scope.adrType, AdrConstants.IMMEDIATE) ||
                        nsADRType.equals($scope.adrType, AdrConstants.ALWAYS) ?
            $scope.adrOptions.get(AdrConstants.TODAY) :
            $scope.adrFutureDate;

        $scope.adr.deliveryDate = $scope.adrFutureDate.formatted;
        updateShipDateText(shipDate, $scope.adrFutureDate);
    }

    function initContractLength() {
        if ($scope.tdc.smallShipTextFields.contractLengthTextSmall && $scope.adrContractLength) {
            $scope.contractLengthText = $scope.tdc.smallShipTextFields.contractLengthTextSmall.replace('{contractLength}', $scope.adrContractLength.displayText);
        }
    }

    function changeContractLength() {
        $scope.adr.contractLength = $scope.adrContractLength.value;
        $scope.adr.contractType = $scope.adrContractLength.contractType;
        $scope.simulateOrderWithElement('contractLength');
        initContractLength();
        if ($scope.adr.contractType.toLowerCase() === AdrConstants.HK_ageLoc_Loyalty) {
            updateAgelocLoyaltyContractTnC();
        } else {
            updateDefaultContractTnC();
        }
    }

    function updateAgelocLoyaltyContractTnC() {
        $scope.tdcAdrTncText = $scope.tdc.agelocloyaltytermsncondition.agelocLoyaltyADRTncText;
        $scope.tncCheckboxText = $scope.tdc.agelocloyaltytermsncondition.agelocLoyaltyADRTncCheckboxText;
        $scope.tncADRURL = $scope.tdc.agelocloyaltytermsncondition.agelocLoyaltyADRTncUrl;
    }

    function updateDefaultContractTnC() {
        $scope.tdcAdrTncText = $scope.tdc.termsncondition.adrTncText;
        $scope.tncCheckboxText = $scope.tdc.termsncondition.adrTncCheckboxText;
        $scope.tncADRURL = $scope.tdc.termsncondition.adrTncUrl;
    }

    function updateShipDateText(shipDate, recurDate) {
        $scope.adr.processAdrText = ($scope.tdc.firstOrderText || '')
            .replace('{shipDay}', getOrdinalDay(shipDate.dayOfMonth, RunConfigService.getRunConfig().language));
        $scope.adr.processAdrText = ($scope.adr.processAdrText || '').replace('{shipMonth}', months[shipDate.month - 1]);
        if (nsADRType.equals($scope.adrType, AdrConstants.IMMEDIATE) ||
            nsADRType.equals($scope.adrType, AdrConstants.ALWAYS)) {
            $scope.adr.processAdrText += ($scope.tdc.recurringOrderTextImmediate || '')
                .replace('{recurDay}', getOrdinalDay(recurDate.dayOfMonth, RunConfigService.getRunConfig().language));
        } else {
            $scope.adr.processAdrText += ($scope.tdc.recurringOrderTextFuture || '')
                .replace('{recurDay}', getOrdinalDay(recurDate.dayOfMonth, RunConfigService.getRunConfig().language));
        }
        if ($scope.tdc.manageYourAdrText) {
            $scope.adr.processAdrText += $scope.tdc.manageYourAdrText;
        }

        // Update the small texts of collapsed section
        $scope.firstShipDayText = ($scope.tdc.smallShipTextFields.firstShipTextSmall || '')
            .replace('{shipDay}', getOrdinalDay(shipDate.dayOfMonth, RunConfigService.getRunConfig().language));
        $scope.firstShipDayText = ($scope.firstShipDayText || '').replace('{shipMonth}', months[shipDate.month - 1]);
        $scope.monthlyShipDayText = ($scope.tdc.smallShipTextFields.recurringShipDayTextSmall || '')
            .replace('{day}', getOrdinalDay(recurDate.dayOfMonth, RunConfigService.getRunConfig().language));
    }

    function getDateChosen(availableDates, desiredDate) {
        let retVal = null;

        if (availableDates) {
            for (let i = 0; i < availableDates.length; i++) {
                if (availableDates[i].dayOfMonth === desiredDate.dayOfMonth) {
                    retVal = availableDates[i];
                }
            }
            if (!retVal) {
                retVal = availableDates[0];
            }
        }

        return retVal;
    }

    function getOrdinalDay(date, lang) {
        let suffix = 'th';
        const day = date instanceof Date ? date.getDate() : date;
        const tenRemainder = day % 10;

        if (lang === 'en') {
            if (day - tenRemainder !== 10) { // 11 is 'th' not 'st'
                switch (tenRemainder) {
                    case 1:
                        suffix = 'st';
                        break;
                    case 2:
                        suffix = 'nd';
                        break;
                    case 3:
                        suffix = 'rd';
                        break;
                    default:
                        suffix = 'th';
                        break;
                }
            }
            return day + suffix;
        } else {
            return day;
        }
    }

    return publicMethods;
}();

export default AdrUtil;
